import {
  getCountries,
  getCountryCallingCode,
  type Country,
} from 'react-phone-number-input';

import { ALLOWED_COUNTRIES } from '@/lib/constants';
import type { Tenant } from '@/state/tenant';

// Copies phoneCountryFor in mokoboko-admin src/lib/phone.ts; change both
// together.

// Reached only by a studio with neither a country nor a business phone saved.
export const FALLBACK_PHONE_COUNTRY: Country = 'PT';

type StudioPhoneSource =
  | Pick<Tenant, 'country' | 'business_phone_country_code'>
  | null
  | undefined;

type SavedPhone =
  | { country_code?: string | null; phone?: string | null }
  | null
  | undefined;

const offered = (iso: string | null | undefined): Country | undefined => {
  const upper = iso?.toUpperCase();
  return ALLOWED_COUNTRIES.find((country) => country === upper);
};

const dialDigits = (code: string) => code.replace(/\D/g, '').replace(/^0+/, '');

// Dial codes are shared (+1 is the US, Canada and a dozen islands), so the
// preferred country settles a tie, then the order this app offers: +1 is never
// Antigua.
const offeredForCode = (
  code: string | null | undefined,
  preferred?: Country
): Country | undefined => {
  if (!code) return undefined;
  if (/^[A-Za-z]{2}$/.test(code)) return offered(code);
  const digits = dialDigits(code);
  if (!digits) return undefined;
  if (preferred && getCountryCallingCode(preferred) === digits) {
    return preferred;
  }
  return ALLOWED_COUNTRIES.find(
    (country) => getCountryCallingCode(country) === digits
  );
};

// Checkout offered every country until February 2026, so a saved number can
// carry a code this app no longer lists. It keeps that country: under the
// studio's, validation refuses the number and a save stores the wrong code.
const unlistedForCode = (
  code: string | null | undefined
): Country | undefined => {
  const digits = code ? dialDigits(code) : '';
  if (!digits) return undefined;
  return (getCountries() as Country[]).find(
    (country) => getCountryCallingCode(country) === digits
  );
};

export const studioPhoneCountry = (tenant: StudioPhoneSource): Country =>
  offered(tenant?.country) ??
  offeredForCode(tenant?.business_phone_country_code) ??
  FALLBACK_PHONE_COUNTRY;

// A person's own number wins, but their code counts only beside a number: the
// backend stores +351 on everyone it saved without a phone.
export const phoneCountryFor = (
  saved: SavedPhone,
  tenant: StudioPhoneSource
): Country => {
  const studio = studioPhoneCountry(tenant);
  if (!saved?.phone?.trim()) return studio;
  return (
    offeredForCode(saved.country_code, studio) ??
    unlistedForCode(saved.country_code) ??
    studio
  );
};
